import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleVancouverStudentHangouts() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "where-students-hangout-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || { articles: [] };
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-social-spots": "Social Spots",
    "section-study-friendly-cafes": "Study-Friendly Cafés",
    "section-budget-friendly": "Budget-Friendly Hangouts",
    "section-late-night": "Late-Night Spots",
    "section-outdoor-locations": "Outdoor Locations",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/parks-recreation-culture.aspx",
      label: "City of Vancouver – Official Visitor Guide",
    },
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver (Tourism Vancouver)",
    },
    {
      url: "https://vpl.bibliocommons.com/",
      label: "Vancouver Public Library",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    // Social Spots Markers
    {
      id: "cambie",
      lat: 49.28306,
      lng: -123.10889,
      popupText: "The Cambie Bar & Grill (Gastown)",
    },
    {
      id: "pit-pub",
      lat: 49.266,
      lng: -123.2495,
      popupText: "The Pit Pub (UBC)",
    },
    // Study-Friendly Cafés Markers
    {
      id: "loafe-cafe",
      lat: 49.26592,
      lng: -123.24959,
      popupText: "Loafe Café (UBC)",
    },
    {
      id: "storm-city-coffee",
      lat: 49.2649,
      lng: -123.185,
      popupText: "Storm City Coffee (Kitsilano)",
    },
    {
      id: "birds-beets",
      lat: 49.28303,
      lng: -123.10612,
      popupText: "The Birds & The Beets (Gastown)",
    },
    // Budget-Friendly Hangouts Markers
    {
      id: "vpl-central",
      lat: 49.27979,
      lng: -123.11567,
      popupText: "Vancouver Public Library – Central Branch",
    },
    {
      id: "granville-island",
      lat: 49.27262,
      lng: -123.13524,
      popupText: "Granville Island Public Market",
    },
    // Late-Night Spots Markers
    {
      id: "breka-bakery",
      lat: 49.2688,
      lng: -123.1853,
      popupText: "Breka Bakery & Café (24/7)",
    },
    {
      id: "duffins",
      lat: 49.23235,
      lng: -123.0768,
      popupText: "Duffin’s Donuts (24/7 Diner)",
    },
    // Outdoor Locations Markers
    {
      id: "stanley-park",
      lat: 49.3,
      lng: -123.14,
      popupText: "Stanley Park",
    },
    {
      id: "kits-beach",
      lat: 49.27427,
      lng: -123.15493,
      popupText: "Kitsilano Beach",
    },
    {
      id: "queen-elizabeth-park",
      lat: 49.24172,
      lng: -123.11276,
      popupText: "Queen Elizabeth Park",
    },
    {
      id: "pacific-spirit-park",
      lat: 49.2644,
      lng: -123.2112,
      popupText: "Pacific Spirit Regional Park (near UBC)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Ultimate Guide to Where Students Hang Out in Vancouver</title>
        <meta
          name="description"
          content="Discover the top student hangout spots in Vancouver—from social pubs to budget-friendly cafes, late-night eats, and scenic outdoor locations."
        />
        <meta
          name="keywords"
          content="Vancouver, student life, hangout spots, budget-friendly, late night, social spots, cafes"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/where-students-hangout-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Ultimate Guide to Where Students Hang Out in Vancouver"
        />
        <meta
          property="og:description"
          content="Find social bars, study-friendly cafes, late-night diners, and free outdoor hangouts perfect for students in Vancouver."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/where-students-hangout-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/where-students-hangout-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Ultimate Guide to Where Students Hang Out in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Discover the best student-friendly spots in Vancouver for studying, socializing, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/where-students-hangout-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Students hanging out in Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is a vibrant, student-friendly city with endless
              opportunities for fun and relaxation. Whether you’re an
              international student or a local, finding the right hangout spots
              can transform your study-life balance. In this guide, we’ll
              explore some of the best locations to socialize, study, or just
              unwind—covering everything from budget-friendly cafes to lively
              bars, late-night diners, and scenic outdoor retreats.
            </p>

            {/* SECTION: Social Spots */}
            <h2 id="section-social-spots">Social Spots</h2>
            <p>
              Looking for a place to meet friends, enjoy cheap drinks, and relax
              away from campus pressure? Vancouver’s nightlife offers several
              student-friendly bars and lounges that won’t break the bank:
            </p>
            <p
              ref={(el) => setSpotRef("cambie", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Cambie Bar &amp; Grill (Gastown)</strong>{" "}
              <Marker
                coordY={49.28306}
                coordX={-123.10889}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              — One of Vancouver’s oldest bars and a backpacker favorite, with
              affordable drinks and a lively atmosphere. Great for meeting new
              people and enjoying cheap pints.
            </p>
            <p
              ref={(el) => setSpotRef("pit-pub", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Pit Pub (UBC)</strong>{" "}
              <Marker
                coordY={49.266}
                coordX={-123.2495}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              — Located in the Student Union Building (the Nest) on UBC campus,
              this pub is famous for mid-week parties and themed events. It’s an
              easy, on-campus option when you want to let loose without a
              commute.
            </p>
            <p>
              Other honorable mentions include Guilt &amp; Company (Gastown) for
              live music, Koerner’s Pub (UBC) for a grad-student vibe, and
              several clubs along Granville Street that host student nights.
              Always remember to bring two pieces of ID—the legal drinking age
              in BC is 19!
            </p>

            {/* SECTION: Study-Friendly Cafés */}
            <h2 id="section-study-friendly-cafes">Study-Friendly Cafés</h2>
            <p>
              Need a change of scenery from the library? These cafés combine
              free Wi-Fi, cozy ambiance, and budget-friendly beverages, making
              them prime spots for study sessions:
            </p>
            <p
              ref={(el) => setSpotRef("loafe-cafe", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Loafe Café (UBC)</strong>{" "}
              <Marker
                coordY={49.26592}
                coordX={-123.24959}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              — Conveniently located in the Robert H. Lee Alumni Centre, Loafe
              provides ample seating, quality coffee, and a mellow setting for
              quiet focus.
            </p>
            <p
              ref={(el) => setSpotRef("storm-city-coffee", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Storm City Coffee (Kitsilano)</strong>{" "}
              <Marker
                coordY={49.2649}
                coordX={-123.185}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              — An independent café popular for its bright interior and friendly
              staff. Perfect for morning study sessions or a quick coffee
              break.
            </p>
            <p
              ref={(el) => setSpotRef("birds-beets", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Birds &amp; The Beets (Gastown)</strong>{" "}
              <Marker
                coordY={49.28303}
                coordX={-123.10612}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              — A trendy café offering artisan coffee and a calm vibe—arrive
              early to secure a table. Great for those who want a smaller,
              cozier space to study.
            </p>
            <p>
              For late-night study sessions, consider Waves Coffee downtown
              (some locations are open 24 hours). Remember café etiquette: buy
              something every few hours to keep your seat guilt-free!
            </p>

            {/* SECTION: Budget-Friendly Hangouts */}
            <h2 id="section-budget-friendly">Budget-Friendly Hangouts</h2>
            <p>
              Vancouver can be expensive, but there are plenty of free or
              low-cost options. The following spots let you have a great time
              without blowing your budget:
            </p>
            <p
              ref={(el) => setSpotRef("vpl-central", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Vancouver Public Library – Central Branch</strong>{" "}
              <Marker
                coordY={49.27979}
                coordX={-123.11567}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              — A stunning building downtown with loads of seating, free Wi-Fi,
              and quiet reading areas for study or group projects. Plus, it’s
              totally free!
            </p>
            <p
              ref={(el) => setSpotRef("granville-island", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Granville Island Public Market</strong>{" "}
              <Marker
                coordY={49.27262}
                coordX={-123.13524}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              — It’s free to explore the market stalls, street performers, and
              artisan shops. Great place to stroll around or grab inexpensive
              snacks.
            </p>
            <p>
              Community centres and campus lounges also offer cheaper drop-in
              activities, from low-cost gym sessions to free clubs and events.
              Look for announcements on student boards or local bulletins—you’ll
              be surprised how many free gatherings pop up!
            </p>

            {/* SECTION: Late-Night Spots */}
            <h2 id="section-late-night">Late-Night Spots</h2>
            <p>
              Whether you’re pulling an all-nighter or looking to satisfy 2am
              cravings, these 24-hour or late-night gems will keep you fueled:
            </p>
            <p
              ref={(el) => setSpotRef("breka-bakery", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Breka Bakery &amp; Café (24/7)</strong>{" "}
              <Marker
                coordY={49.2688}
                coordX={-123.1853}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              — With multiple locations open 24 hours, Breka is the go-to for
              coffee, pastries, and Wi-Fi at any hour of the day (or night).
            </p>
            <p
              ref={(el) => setSpotRef("duffins", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Duffin’s Donuts (24/7 Diner)</strong>{" "}
              <Marker
                coordY={49.23235}
                coordX={-123.0768}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              — A cult favorite in East Vancouver, serving donuts, fried chicken,
              and Vietnamese sandwiches around the clock. Expect a diverse
              crowd, especially after midnight!
            </p>
            <p>
              Night owls can also find 24-hour fast-food options like McDonald’s
              or check out Denny’s for all-night breakfast. Just be mindful of
              reduced late-night transit schedules (NightBus or ride-sharing),
              and stay safe.
            </p>

            {/* SECTION: Outdoor Locations */}
            <h2 id="section-outdoor-locations">Outdoor Locations</h2>
            <p>
              Vancouver’s natural beauty is one of its biggest draws. When
              you’re itching to get outside, consider these scenic escapes:
            </p>
            <p
              ref={(el) => setSpotRef("stanley-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Stanley Park</strong>{" "}
              <Marker
                coordY={49.3}
                coordX={-123.14}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              — 1,000 acres of forest, beaches, and walking trails right beside
              downtown. Bike the seawall, explore the totem poles, or enjoy a
              picnic by the water.
            </p>
            <p
              ref={(el) => setSpotRef("kits-beach", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Kitsilano Beach</strong>{" "}
              <Marker
                coordY={49.27427}
                coordX={-123.15493}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              — A lively beach close to downtown and near plenty of cafés and
              eateries. Perfect for volleyball, sunbathing, or just hanging with
              friends.
            </p>
            <p
              ref={(el) => setSpotRef("queen-elizabeth-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Queen Elizabeth Park</strong>{" "}
              <Marker
                coordY={49.24172}
                coordX={-123.11276}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              — Elevated park with stunning city views, manicured gardens, and
              the Bloedel Conservatory (small fee for entry).
            </p>
            <p
              ref={(el) => setSpotRef("pacific-spirit-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Pacific Spirit Regional Park</strong>{" "}
              <Marker
                coordY={49.2644}
                coordX={-123.2112}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              — Surrounding UBC’s campus, this forested park offers trails for
              hiking, biking, and a quick escape into nature. Wreck Beach is
              also nearby—famous for its clothing-optional status and dramatic
              sunsets.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. What is the legal drinking age in Vancouver?</h3>
              <p>
                The legal drinking age in British Columbia is 19. Always carry
                two pieces of ID when going to bars or buying alcohol.
              </p>

              <h3>2. Are there places to study 24/7?</h3>
              <p>
                Yes! Breka Bakery &amp; Café (multiple locations) is open 24/7,
                perfect for late-night study sessions. Also, some Waves Coffee
                shops and fast-food joints downtown operate around the clock.
              </p>

              <h3>3. What are some free activities for students on a budget?</h3>
              <p>
                Take advantage of Vancouver’s beaches, parks, and the Vancouver
                Public Library. Granville Island is also free to explore, and
                many universities host free or low-cost events throughout the
                year.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              <strong>Drinking &amp; Cannabis:</strong> You must be 19+ to
              purchase or consume alcohol or cannabis in BC. Drinking is only
              allowed in licensed venues or designated public spaces (some
              beaches and parks). <br />
              <strong>Noise Bylaws:</strong> Generally, quiet hours start around
              10pm in residential areas. Be respectful when partying or heading
              home late at night. <br />
              <strong>Park Hours &amp; Safety:</strong> Most city parks close
              between 10pm and 11pm. Stick to well-lit areas when walking at
              night and stay aware of surroundings. <br />
              <strong>Transit:</strong> SkyTrain stops around 12:30am. NightBus
              routes operate after that—check the schedule before staying out
              late.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              From bustling bars to tranquil beaches, Vancouver has no shortage
              of fantastic student hangout spots. Whether you’re looking to
              socialize, cram for finals, or soak up the scenery, this city
              offers something for every mood and budget. Remember to stay safe,
              be mindful of local regulations, and enjoy exploring the
              multicultural energy that makes Vancouver a top destination for
              students from around the world!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}